/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useToast } from '@/hooks/use-toast';
import { useApp } from '@/contexts/AppContext';
import {
  readCurrentAgentsMd,
  applyAgentsMd,
  generateAgentsMd,
  readCustomRules,
  saveCustomRules,
} from '@/services/skillshop';
import { AgentsMdViewer } from './AgentsMdViewer';
import {
  FileCode,
  RefreshCw,
  Loader2,
  Package,
  Sparkles,
  ChevronDown,
  ChevronRight,
  Save,
  BookOpen,
} from 'lucide-react';

export function AgentsMdTab() {
  const { activeProduct, activeRepoUrl } = useApp();
  const { toast } = useToast();

  // Current AGENTS.md state
  const [currentContent, setCurrentContent] = useState<string | null>(null);
  const [currentPath, setCurrentPath] = useState<string | undefined>();
  const [currentLastModified, setCurrentLastModified] = useState<string | undefined>();
  const [currentExists, setCurrentExists] = useState(false);
  const [isLoadingCurrent, setIsLoadingCurrent] = useState(false);

  // Custom rules state
  const [customRules, setCustomRules] = useState<string>('');
  const [originalCustomRules, setOriginalCustomRules] = useState<string>('');
  const [isLoadingRules, setIsLoadingRules] = useState(false);
  const [isSavingRules, setIsSavingRules] = useState(false);
  const [rulesExpanded, setRulesExpanded] = useState(true);

  // Generate state
  const [isGenerating, setIsGenerating] = useState(false);

  // Get the repo path for the active product
  const repoPath = activeProduct?.repoPath;

  // Check if custom rules have unsaved changes
  const hasUnsavedRules = customRules !== originalCustomRules;

  // Fetch current AGENTS.md
  const fetchCurrentAgentsMd = useCallback(async () => {
    if (!activeRepoUrl) return;

    setIsLoadingCurrent(true);
    try {
      const result = await readCurrentAgentsMd(activeRepoUrl, repoPath ?? undefined);
      setCurrentExists(result.exists);
      setCurrentContent(result.content ?? null);
      setCurrentPath(result.path);
      setCurrentLastModified(result.lastModified);
    } catch (error: any) {
      console.error('Error fetching current AGENTS.md:', error);
      toast({
        title: 'Error loading AGENTS.md',
        description: error.message || 'Failed to load current AGENTS.md',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingCurrent(false);
    }
  }, [activeRepoUrl, repoPath, toast]);

  // Fetch custom rules
  const fetchCustomRules = useCallback(async () => {
    if (!activeRepoUrl) return;

    setIsLoadingRules(true);
    try {
      const result = await readCustomRules(activeRepoUrl, repoPath ?? undefined);
      const content = result.content ?? '';
      setCustomRules(content);
      setOriginalCustomRules(content);
    } catch (error: any) {
      console.error('Error fetching custom rules:', error);
      // Don't show error toast for missing rules file - it's expected
    } finally {
      setIsLoadingRules(false);
    }
  }, [activeRepoUrl, repoPath]);

  // Save custom rules
  const handleSaveRules = async () => {
    if (!activeRepoUrl) return;

    setIsSavingRules(true);
    try {
      const result = await saveCustomRules(activeRepoUrl, customRules, repoPath ?? undefined);
      if (result.success) {
        setOriginalCustomRules(customRules);
        toast({
          title: 'Custom Rules Saved',
          description: 'Your custom rules have been saved to data/product/rules.md',
        });
      } else {
        throw new Error(result.error || 'Failed to save custom rules');
      }
    } catch (error: any) {
      toast({
        title: 'Error saving custom rules',
        description: error.message || 'Failed to save custom rules',
        variant: 'destructive',
      });
    } finally {
      setIsSavingRules(false);
    }
  };

  // Handle generate new AGENTS.md - directly applies without modal
  const handleGenerate = async () => {
    if (!activeProduct?.id || !activeRepoUrl) {
      toast({
        title: 'No product selected',
        description: 'Please select a product to generate AGENTS.md',
        variant: 'destructive',
      });
      return;
    }
    
    setIsGenerating(true);
    try {
      // Generate AGENTS.md
      const result = await generateAgentsMd(activeProduct.id, {
        includePlaybooks: true,
        includeSkills: true,
        format: 'full',
      });
      
      if (!result.content) {
        throw new Error('No content generated');
      }
      
      // Directly apply to worktree
      const applyResult = await applyAgentsMd(activeRepoUrl, result.content, repoPath ?? undefined);
      
      if (applyResult.success) {
        toast({
          title: 'AGENTS.md Generated & Applied',
          description: `Generated with ${result.includedPlaybooks?.length ?? 0} playbooks. Applied to worktree - commit changes in the Changes page.`,
        });
        // Refresh current content
        await fetchCurrentAgentsMd();
      } else {
        throw new Error(applyResult.error || 'Failed to apply AGENTS.md');
      }
    } catch (error: any) {
      toast({
        title: 'Error generating AGENTS.md',
        description: error.message || 'Failed to generate AGENTS.md',
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Load data on mount and when product changes
  useEffect(() => {
    fetchCurrentAgentsMd();
    fetchCustomRules();
  }, [fetchCurrentAgentsMd, fetchCustomRules]);

  // No product selected state
  if (!activeProduct) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <Package className="h-16 w-16 text-muted-foreground/50 mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No Product Selected</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            Select an active product to view and manage its AGENTS.md file.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="flex items-center gap-1">
            <Package className="h-3 w-3" />
            {activeProduct.title}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              fetchCurrentAgentsMd();
              fetchCustomRules();
            }}
            disabled={isLoadingCurrent || isLoadingRules}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoadingCurrent || isLoadingRules ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={handleGenerate}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 mr-2" />
            )}
            Generate & Apply
          </Button>
        </div>
      </div>

      {/* Custom Rules Section */}
      <Card>
        <Collapsible open={rulesExpanded} onOpenChange={setRulesExpanded}>
          <CardHeader className="pb-3">
            <CollapsibleTrigger className="flex items-center justify-between w-full">
              <CardTitle className="flex items-center gap-2 text-lg">
                <BookOpen className="h-5 w-5 text-primary" />
                Project-Specific Guidelines
                {hasUnsavedRules && (
                  <Badge variant="secondary" className="ml-2">Unsaved</Badge>
                )}
              </CardTitle>
              {rulesExpanded ? (
                <ChevronDown className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              )}
            </CollapsibleTrigger>
            <p className="text-sm text-muted-foreground mt-1">
              Custom rules and guidelines that will be included in the generated AGENTS.md.
              Stored in <code className="text-xs bg-muted px-1 rounded">data/product/rules.md</code>
            </p>
          </CardHeader>
          <CollapsibleContent>
            <CardContent className="pt-0">
              {isLoadingRules ? (
                <div className="space-y-3">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-4/5" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              ) : (
                <div className="space-y-3">
                  <Textarea
                    placeholder="Enter project-specific rules and guidelines in Markdown format...

Example:
- Always use TypeScript strict mode
- Follow the naming convention: camelCase for variables, PascalCase for components
- All API endpoints must have error handling"
                    value={customRules}
                    onChange={(e) => setCustomRules(e.target.value)}
                    className="min-h-[200px] font-mono text-sm"
                  />
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      onClick={handleSaveRules}
                      disabled={isSavingRules || !hasUnsavedRules}
                    >
                      {isSavingRules ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      Save Rules
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      {/* Current AGENTS.md Section */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <FileCode className="h-5 w-5" />
          Current AGENTS.md
        </h3>

        {isLoadingCurrent ? (
          <Card>
            <CardContent className="py-6">
              <div className="space-y-3">
                <Skeleton className="h-6 w-1/3" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-4/5" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            </CardContent>
          </Card>
        ) : currentExists && currentContent ? (
          <AgentsMdViewer
            content={currentContent}
            title="Product AGENTS.md"
            subtitle="Currently deployed in repository root"
            path={currentPath}
            lastModified={currentLastModified}
          />
        ) : (
          <Card>
            <CardContent className="py-8">
              <div className="text-center">
                <FileCode className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                <h4 className="font-medium mb-2">No AGENTS.md Found</h4>
                <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
                  This product doesn't have an AGENTS.md file yet. Add your custom rules above,
                  then click "Generate & Apply" to create one from your playbooks and skills.
                </p>
                <Button onClick={handleGenerate} disabled={isGenerating}>
                  {isGenerating ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4 mr-2" />
                  )}
                  Generate & Apply AGENTS.md
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
